import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowRightLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const num = (n) => Math.round(n).toLocaleString();
const fmtD = (s) => {
  if (!s) return "—";
  const d = new Date(s + "T00:00:00");
  return isNaN(d) ? s : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// Allocation ledger: which open sales orders hold this finished good, with manual reassignment.
export function SOReservationsDialog({ soKey, soName, fg, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [onHand, setOnHand] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reassign, setReassign] = useState(null); // { from: row, toLineId, qty }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!fg?.product_id) return;
    setLoading(true);
    try {
      const { data } = await api.get("/sales-orders/fg-allocations", {
        params: { product_id: fg.product_id, design_id: fg.design_id || "", case_pack: fg.case_pack },
      });
      setRows(data.rows || []);
      setOnHand(data.on_hand || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  }, [fg?.product_id, fg?.design_id, fg?.case_pack]);

  useEffect(() => { if (soKey) { setReassign(null); load(); } }, [soKey, load]);

  const targets = reassign ? rows.filter((r) => r.line_id !== reassign.from.line_id && r.remaining_cases > 0.009) : [];

  const doReassign = async () => {
    const qty = Number(reassign.qty) || 0;
    const to = rows.find((r) => r.line_id === reassign.toLineId);
    if (!to || qty <= 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/sales-orders/reassign-stock", {
        from_so_id: reassign.from.so_id, from_line_id: reassign.from.line_id,
        to_so_id: to.so_id, to_line_id: to.line_id, cases: qty,
      });
      toast.success(`${num(data.cases)} cases moved ${data.from_so} → ${data.to_so}. Shortages recalculated.`);
      setReassign(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const totalReserved = rows.reduce((a, r) => a + r.reserved_cases, 0);

  return (
    <Dialog open={!!soKey} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-3xl" data-testid="so-reservations-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Stock Allocation</DialogTitle>
          <DialogDescription>{soName} — which open orders hold this stock. Reassign reserved cases between orders when priorities change.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#002FA7]" /></div>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">No open sales orders need this item.</p>
        ) : (
          <>
            <table className="w-full border-collapse text-sm" data-testid="so-reservations-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.08em] text-slate-500">
                  <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">SO</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">Customer</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">Ship Date</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right font-semibold">Ordered</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right font-semibold">Holds</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right font-semibold">Needs</th>
                  <th className="border-b border-slate-200 px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.line_id}-${i}`} className="text-slate-700" data-testid={`so-reservation-row-${i}`}>
                    <td className="whitespace-nowrap border-b border-slate-100 px-2 py-2 font-semibold">
                      <Link to="/sales-orders" className="text-[#002FA7] hover:underline">{r.so_number}</Link>
                    </td>
                    <td className="border-b border-slate-100 px-2 py-2">{r.customer_name || "—"}</td>
                    <td className="whitespace-nowrap border-b border-slate-100 px-2 py-2">{fmtD(r.ship_date)}</td>
                    <td className="border-b border-slate-100 px-2 py-2 text-right tabular-nums">{num(r.line_cases)}</td>
                    <td className="border-b border-slate-100 px-2 py-2 text-right font-semibold tabular-nums" data-testid={`alloc-holds-${i}`}>
                      {r.reserved_cases > 0 ? num(r.reserved_cases) : <span className="font-normal text-slate-300">0</span>}
                    </td>
                    <td className="border-b border-slate-100 px-2 py-2 text-right tabular-nums">
                      {r.remaining_cases > 0.009 ? <span className={`font-semibold ${r.shortage_cases > 0.009 ? "text-red-600" : "text-slate-600"}`} data-testid={`alloc-short-${i}`}>{num(r.remaining_cases)}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="border-b border-slate-100 px-2 py-2 text-right">
                      {r.reserved_cases > 0.009 && (
                        <button onClick={() => setReassign({ from: r, toLineId: "", qty: "" })}
                          className="inline-flex items-center gap-1 rounded-sm border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]"
                          data-testid={`reassign-btn-${i}`}>
                          <ArrowRightLeft className="h-3 w-3" /> Reassign
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="text-slate-900">
                  <td className="px-2 py-2 text-xs font-semibold uppercase tracking-wide" colSpan={4}>Total reserved / on hand</td>
                  <td className="px-2 py-2 text-right font-bold tabular-nums" data-testid="so-reservations-total">{num(totalReserved)}</td>
                  <td className="px-2 py-2 text-right text-xs tabular-nums text-slate-500" colSpan={2}>{num(onHand)} on hand</td>
                </tr>
              </tbody>
            </table>

            {reassign && (
              <div className="rounded-sm border border-[#002FA7]/30 bg-[#002FA7]/[0.03] p-3" data-testid="reassign-panel">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Move cases from {reassign.from.so_number} ({num(reassign.from.reserved_cases)} held)
                </p>
                {targets.length === 0 ? (
                  <p className="text-sm text-slate-500">No other open order of this item is short — nothing to move stock to.</p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Input type="text" inputMode="numeric" value={reassign.qty}
                      onChange={(e) => setReassign({ ...reassign, qty: e.target.value.replace(/[^0-9.]/g, "") })}
                      placeholder="Cases" className="h-9 w-28 rounded-sm text-right tabular-nums" data-testid="reassign-qty" />
                    <span className="text-sm text-slate-500">cases to</span>
                    <Select value={reassign.toLineId || undefined} onValueChange={(v) => setReassign({ ...reassign, toLineId: v })}>
                      <SelectTrigger className="h-9 w-64 rounded-sm" data-testid="reassign-target"><SelectValue placeholder="Select order" /></SelectTrigger>
                      <SelectContent>
                        {targets.map((t) => (
                          <SelectItem key={t.line_id} value={t.line_id}>
                            {t.so_number} — {t.customer_name || "No customer"} (needs {num(t.remaining_cases)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" disabled={busy || !reassign.toLineId || !(Number(reassign.qty) > 0)} onClick={doReassign}
                      className="h-9 rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="reassign-confirm">
                      {busy ? "Moving…" : "Move Stock"}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-9 rounded-sm" onClick={() => setReassign(null)} data-testid="reassign-cancel">Cancel</Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        <p className="text-right text-xs">
          <Link to="/sales-orders" className="font-semibold text-[#002FA7] hover:underline" data-testid="so-open-page">Open Sales Orders →</Link>
        </p>
      </DialogContent>
    </Dialog>
  );
}
